import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiUsers, FiUser, FiMail, FiLoader, FiCheck, FiAlertCircle } = FiIcons;

const StartScreen = ({ onSubmit }) => {
  const [ticketPromedio, setTicketPromedio] = useState('');
  const [leadsMenuales, setLeadsMenuales] = useState('');
  const [errors, setErrors] = useState({});
  
  // Lead capture form states
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [leadErrors, setLeadErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Handle lead capture form submission
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'Ingresa tu nombre';
    }
    if (!email.trim()) {
      newErrors.email = 'Ingresa tu correo';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Ingresa un correo válido';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setLeadErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch('https://services.leadconnectorhq.com/hooks/0yNW04bxSDmn243guLoT/webhook-trigger/113e1573-7548-47e4-9fe0-a32b3b3fd78a', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          first_name: firstName,
          source: 'sinfugas.titan10x.com'
        })
      });
      
      if (response.ok) {
        setLeadSubmitted(true);
        setLeadErrors({});
      } else {
        throw new Error('Error al enviar datos');
      }
    } catch (error) {
      setSubmitError('Hubo un problema al registrar tus datos, intenta de nuevo en unos momentos');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCalculatorSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const ticket = parseFloat(ticketPromedio);
    const leads = parseInt(leadsMenuales);

    if (!ticket || ticket <= 0) {
      newErrors.ticket = 'Ingresa un valor válido mayor a 0';
    }
    if (!leads || leads <= 0) {
      newErrors.leads = 'Ingresa un número válido mayor a 0';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({ ticketPromedio: ticket, leadsMenuales: leads });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-12">
          {/* Updated Titan Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://storage.googleapis.com/msgsndr/0yNW04bxSDmn243guLoT/media/6901a9298cdf03a50ab1cf7b.svg" 
              alt="TITAN10X" 
              className="h-24 md:h-32 object-contain filter drop-shadow-xl" 
            />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            <span className="text-orange-500">Detector de Fugas</span> de Ingresos 24/7
          </h2>
          <p className="text-xl text-gray-300 max-w-lg mx-auto">
            En 60 segundos calcula cuánto dinero pierdes por no tener seguimiento automatizado.
          </p>
        </div>

        {/* Lead Capture Form */}
        <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl relative overflow-hidden mb-6">
          {/* Decorative gradient blob */}
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
          
          {!leadSubmitted ? (
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-4 text-center">
                🔍 Descubre tus fugas de ingresos
              </h3>
              <p className="text-lg text-gray-300 mb-6 text-center">
                Al completar tu nombre y correo, iniciarás un cálculo revelador sobre cuánto dinero estás perdiendo y obtendrás claridad total sobre tus fugas de ingresos.
              </p>
              
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                <div>
                  <label className="flex items-center text-lg font-semibold text-white mb-3">
                    <SafeIcon icon={FiUser} className="text-orange-500 mr-2" />
                    Nombre completo
                  </label>
                  <input 
                    type="text" 
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Ej: Juan Pérez" 
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all" 
                  />
                  {leadErrors.firstName && (
                    <p className="text-red-400 text-sm mt-2">{leadErrors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <label className="flex items-center text-lg font-semibold text-white mb-3">
                    <SafeIcon icon={FiMail} className="text-pink-500 mr-2" />
                    Correo electrónico
                  </label>
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ejemplo@correo.com" 
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-lg focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 transition-all" 
                  />
                  {leadErrors.email && (
                    <p className="text-red-400 text-sm mt-2">{leadErrors.email}</p>
                  )}
                </div>
                
                {submitError && (
                  <div className="flex items-center p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <SafeIcon icon={FiAlertCircle} className="text-red-400 mr-2" />
                    <p className="text-red-400 text-sm">{submitError}</p>
                  </div>
                )}
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold py-4 px-8 rounded-xl text-xl hover:from-orange-600 hover:to-pink-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <FiLoader className="animate-spin mr-2" />
                      Enviando...
                    </>
                  ) : (
                    '🔍 Iniciar cálculo revelador'
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="relative z-10 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="text-green-400 text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                ¡Datos registrados!
              </h3>
              <p className="text-gray-300 mb-4">
                Ahora vamos a calcular tus fugas de ingresos
              </p>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-gradient-to-r from-orange-500 to-pink-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          )}
        </div>

        {/* Calculator Form - Only show after lead is submitted */}
        {leadSubmitted && (
          <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl relative overflow-hidden">
            {/* Decorative gradient blob */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <form onSubmit={handleCalculatorSubmit} className="space-y-6 relative z-10">
              <h3 className="text-xl font-bold text-white mb-4">
                💸 Ahora, calculemos tus pérdidas
              </h3>
              
              <div>
                <label className="flex items-center text-lg font-semibold text-white mb-3">
                  <SafeIcon icon={FiDollarSign} className="text-orange-500 mr-2" />
                  Valor promedio de un cliente (MXN)
                </label>
                <input 
                  type="number" 
                  value={ticketPromedio}
                  onChange={(e) => setTicketPromedio(e.target.value)}
                  placeholder="Ej: 5000" 
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all" 
                />
                {errors.ticket && (
                  <p className="text-red-400 text-sm mt-2">{errors.ticket}</p>
                )}
              </div>

              <div>
                <label className="flex items-center text-lg font-semibold text-white mb-3">
                  <SafeIcon icon={FiUsers} className="text-pink-500 mr-2" />
                  Leads mensuales aproximados
                </label>
                <input 
                  type="number" 
                  value={leadsMenuales}
                  onChange={(e) => setLeadsMenuales(e.target.value)}
                  placeholder="Ej: 50" 
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-lg focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 transition-all" 
                />
                {errors.leads && (
                  <p className="text-red-400 text-sm mt-2">{errors.leads}</p>
                )}
              </div>

              <button 
                type="submit" 
                className="w-full bg-gradient-to-r from-red-500 to-orange-600 text-white font-bold py-4 px-8 rounded-xl text-xl hover:from-red-600 hover:to-orange-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-red-500/20"
              >
                💸 Calcular mis fugas
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartScreen;